import React, {useContext, useEffect, useState, useRef} from 'react';
import './AddToHomeScreenPrompt.scss';
import {Button} from "../../Form/Button/Button";
import {Modal} from "../Modal/Modal";
import {I18nReactContext} from "../../../contexts/I18nContext";
import {UserReactContext} from "../../../contexts/UserContext";
import {updatePwaPromptDismissalAPI} from "../../../Services/api";
import {get} from "lodash";
import {Checkbox, FormControlLabel} from "@mui/material";
import {loadUserStateFromLocalStorage, setUserState} from "../../../Utils/User/user";

/**
 * AddToHomeScreenPrompt - Prompts users to install the PWA on their device
 * Uses backend tracking via preferences to persist dismissal across devices
 * Detects iOS and Android to show appropriate installation instructions
 * Includes "Never ask again" option that permanently dismisses the prompt
 */
export const AddToHomeScreenPrompt = () => {
    const {i18n} = useContext(I18nReactContext);
    const {user} = useContext(UserReactContext);
    const [showPrompt, setShowPrompt] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [neverAskAgain, setNeverAskAgain] = useState(false);

    // Store deferred prompt in ref to persist across renders and capture it before user logs in
    const deferredPromptRef = useRef(null);

    // Effect 1: Capture the beforeinstallprompt event immediately (before login)
    // This event only fires once when the page loads, so we must capture it early
    useEffect(() => {
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            // Store the event for later use (after user logs in)
            deferredPromptRef.current = e;
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    // Effect 2: Show prompt only after user is logged in and we have permission
    useEffect(() => {
        // Wait for user to be logged in
        if (!user || !user.id) {
            return;
        }

        // Get PWA prompt status from user object (returned from login)
        const pwaPromptStatus = get(user, 'pwaPromptStatus', {shouldShow: true});

        // Check if already installed (running in standalone mode)
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches
            || window.navigator.standalone
            || document.referrer.includes('android-app://');

        // Detect iOS
        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        setIsIOS(isIOSDevice);

        // Only show if backend says we should show, not installed, and on mobile
        if (pwaPromptStatus.shouldShow && !isStandalone && isMobileDevice()) {
            // For Android - check if we already captured the beforeinstallprompt event
            if (!isIOSDevice && deferredPromptRef.current) {
                setShowPrompt(true);
            }

            // For iOS - show immediately (no native prompt event exists)
            if (isIOSDevice) {
                setShowPrompt(true);
            }
        }
    }, [user]);

    /**
     * Checks if device is mobile
     */
    const isMobileDevice = () => {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };

    /**
     * Handles install button click
     * For Android: triggers native install prompt
     * For iOS: shows instructions
     */
    const handleInstallClick = async () => {
        if (deferredPromptRef.current && !isIOS) {
            // Android - trigger native install prompt
            deferredPromptRef.current.prompt();
            const {outcome} = await deferredPromptRef.current.userChoice;

            if (outcome === 'accepted') {
                console.log('User accepted PWA install');
            }

            deferredPromptRef.current = null;
        }

        // For iOS, modal stays open to show instructions
        if (!isIOS) {
            handleDismiss();
        }
    };

    /**
     * Handles dismissing the prompt and saves to backend
     * Also updates localStorage to persist dismissal across page refreshes
     */
    const handleDismiss = async () => {
        try {
            await updatePwaPromptDismissalAPI({neverAskAgain});

            // Update localStorage with new pwaPromptStatus to persist across refreshes
            const userState = loadUserStateFromLocalStorage();
            if (userState && userState.user) {
                userState.user.pwaPromptStatus = {shouldShow: false};
                setUserState({user: userState.user, token: userState.token});
            }

            setShowPrompt(false);
        } catch (error) {
            console.error('Error updating PWA prompt dismissal:', error);
            // Still close the prompt even if API fails
            setShowPrompt(false);
        }
    };

    if (!showPrompt) {
        return null;
    }

    return (
        <Modal open={showPrompt} onClose={handleDismiss}>
            <div className="AddToHomeScreenPrompt modalWrapper">
                <div className="modalTitle">
                    <div className="AddToHomeScreenPrompt__title">
                        <span className="AddToHomeScreenPrompt__icon">📱</span>
                        {i18n.translate('addToHomeScreen.title')}
                    </div>
                </div>

                <div className="modalContent AddToHomeScreenPrompt__content">
                    <p className="AddToHomeScreenPrompt__description">
                        {i18n.translate('addToHomeScreen.description')}
                    </p>

                    {isIOS ? (
                        <div className="AddToHomeScreenPrompt__instructions">
                            <p className="AddToHomeScreenPrompt__instructions-title">
                                {i18n.translate('addToHomeScreen.iosInstructions.title')}
                            </p>
                            <ol className="AddToHomeScreenPrompt__steps">
                                <li>
                                    <span className="AddToHomeScreenPrompt__step-icon">1.</span>
                                    <span className="AddToHomeScreenPrompt__step-text">
                                        {i18n.translate('addToHomeScreen.iosInstructions.step1Part1')}
                                        <span className="AddToHomeScreenPrompt__share-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zm4 5v11c0 1.1-.9 2-2 2H6c-1.11 0-2-.9-2-2V10c0-1.11.89-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .89 2 2z"/>
                                            </svg>
                                        </span>
                                        {i18n.translate('addToHomeScreen.iosInstructions.step1Part2')}
                                        <span className="AddToHomeScreenPrompt__dots-icon">⋯</span>
                                    </span>
                                </li>
                                <li>
                                    <span className="AddToHomeScreenPrompt__step-icon">2.</span>
                                    <span className="AddToHomeScreenPrompt__step-text">
                                        {i18n.translate('addToHomeScreen.iosInstructions.step2')}
                                    </span>
                                </li>
                                <li>
                                    <span className="AddToHomeScreenPrompt__step-icon">3.</span>
                                    <span className="AddToHomeScreenPrompt__step-text">
                                        {i18n.translate('addToHomeScreen.iosInstructions.step3')}
                                        <span className="AddToHomeScreenPrompt__add-icon">➕</span>
                                    </span>
                                </li>
                                <li>
                                    <span className="AddToHomeScreenPrompt__step-icon">4.</span>
                                    <span className="AddToHomeScreenPrompt__step-text">
                                        {i18n.translate('addToHomeScreen.iosInstructions.step4')}
                                    </span>
                                </li>
                            </ol>
                        </div>
                    ) : (
                        <div className="AddToHomeScreenPrompt__benefits">
                            <ul>
                                <li>✓ {i18n.translate('addToHomeScreen.benefits.faster')}</li>
                                <li>✓ {i18n.translate('addToHomeScreen.benefits.offline')}</li>
                                <li>✓ {i18n.translate('addToHomeScreen.benefits.homeScreen')}</li>
                            </ul>
                        </div>
                    )}
                </div>

                <div className="modalFooter AddToHomeScreenPrompt__footer">
                    <div className="AddToHomeScreenPrompt__footer-content">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={neverAskAgain}
                                    onChange={(e) => setNeverAskAgain(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={i18n.translate('addToHomeScreen.neverAskAgain')}
                        />
                        <div className="AddToHomeScreenPrompt__footer-buttons">
                            {!isIOS && (
                                <Button
                                    onClick={handleInstallClick}
                                    color="primary"
                                    text={i18n.translate('addToHomeScreen.install')}
                                />
                            )}
                            <Button
                                onClick={handleDismiss}
                                color={isIOS ? "primary" : "secondary"}
                                text={i18n.translate('addToHomeScreen.dismiss')}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};
